import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, PenLine, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Notifications = ({ user }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ unread_messages: 0, consignes_to_sign: 0, unread_announcements: 0 });
  const canViewAnnouncements = user?.permission_overrides?.view_announcements ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadCounts = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${BACKEND_URL}/api/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          setCounts(await response.json());
        }
      } catch { /* silencieux, non bloquant */ }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const total = (counts.unread_messages || 0) + (counts.consignes_to_sign || 0) + (canViewAnnouncements ? (counts.unread_announcements || 0) : 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white hover:bg-white/10 hover:text-white"
          data-testid="notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {total > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs border-2 border-black"
            >
              {total > 9 ? '9+' : total}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            if (counts.latest_unread_contact_id) {
              navigate('/messagerie', { state: { openContactId: counts.latest_unread_contact_id } });
            } else {
              navigate('/messagerie');
            }
          }}
          className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
          data-testid="notif-goto-messagerie"
        >
          <span className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <MessageSquare className="h-4 w-4" />
            Messages non lus
          </span>
          <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${counts.unread_messages > 0 ? 'bg-blue-500 text-white' : 'text-slate-400'}`}>
            {counts.unread_messages || 0}
          </span>
        </button>
        {(user?.permission_overrides?.sign_consignes ?? ['admin', 'chef_poste', 'agent'].includes(user?.role)) && (
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/consignes'); }}
            className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
            data-testid="notif-goto-consignes"
          >
            <span className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <PenLine className="h-4 w-4" />
              Consignes à signer
            </span>
            <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${counts.consignes_to_sign > 0 ? 'bg-orange-500 text-white' : 'text-slate-400'}`}>
              {counts.consignes_to_sign || 0}
            </span>
          </button>
        )}
        {canViewAnnouncements && (
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/messagerie', { state: { openTab: 'announcements' } }); }}
            className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
            data-testid="notif-goto-announcements"
          >
            <span className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <Megaphone className="h-4 w-4" />
              Communication Entreprise
            </span>
            <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${counts.unread_announcements > 0 ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}>
              {counts.unread_announcements || 0}
            </span>
          </button>
        )}
        {total === 0 && (
          <p className="text-xs text-slate-400 text-center py-2">Aucune notification</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
